import { GrElement, TOKENS_CSS } from "../core/base";

/**
 * <gr-gauge topic="co2/co2" label="CO₂" unit="ppm" min="400" max="2000"
 *           zones="800,1400"></gr-gauge>
 *
 * Numeric readout with an arc gauge. Optional `zones` splits the range
 * into ok/warn/bad colors at the given thresholds.
 */
export class GrGauge extends GrElement {
  private value: number | null = null;

  connectedCallback(): void {
    const root = this.attachShadow({ mode: "open" });
    root.innerHTML = `
      <style>
        ${TOKENS_CSS}
        :host {
          display: inline-flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 18px;
          border-radius: var(--_radius);
          background: var(--_bg);
          border: 1px solid var(--_border);
          min-width: 130px;
        }
        svg { display: block; }
        .arc-bg { stroke: rgba(255, 255, 255, 0.08); }
        .arc-val { transition: stroke-dashoffset 0.3s ease, stroke 0.3s ease; }
        .value {
          font-size: 26px;
          font-weight: 700;
          letter-spacing: -0.03em;
          font-variant-numeric: tabular-nums;
        }
        .unit { color: var(--_muted); font-size: 11px; margin-left: 4px; font-weight: 400; }
      </style>
      <span class="label"></span>
      <svg width="110" height="66" viewBox="0 0 110 66">
        <path class="arc-bg" d="M 10 60 A 45 45 0 0 1 100 60" fill="none" stroke-width="8" stroke-linecap="round"/>
        <path class="arc-val" d="M 10 60 A 45 45 0 0 1 100 60" fill="none" stroke-width="8" stroke-linecap="round"/>
      </svg>
      <div class="value"><span class="num">—</span><span class="unit"></span></div>
    `;
    root.querySelector(".label")!.textContent = this.attr("label", this.attr("topic"));
    root.querySelector(".unit")!.textContent = this.attr("unit");
    this.renderValue();
    this.track(
      this.bus.subscribe(this.attr("topic"), (msg) => {
        const num = Number(msg.payload);
        if (!Number.isNaN(num)) {
          this.value = num;
          this.renderValue();
        }
      }),
    );
  }

  private renderValue(): void {
    const root = this.shadowRoot;
    if (!root) return;
    const min = this.numAttr("min", 0);
    const max = this.numAttr("max", 100);
    const arc = root.querySelector<SVGPathElement>(".arc-val")!;
    const num = root.querySelector(".num")!;

    if (this.value === null) {
      arc.style.strokeDasharray = "141.4";
      arc.style.strokeDashoffset = "141.4";
      num.textContent = "—";
      return;
    }

    const digits = this.numAttr("digits", Number.isInteger(this.value) ? 0 : 1);
    num.textContent = this.value.toFixed(digits);

    const ratio = Math.min(1, Math.max(0, (this.value - min) / (max - min || 1)));
    const arcLength = 141.4; // half-circle of r=45
    arc.style.strokeDasharray = String(arcLength);
    arc.style.strokeDashoffset = String(arcLength * (1 - ratio));
    arc.style.stroke = this.zoneColor(this.value);
  }

  private zoneColor(value: number): string {
    const zones = this.attr("zones");
    if (!zones) return "var(--_accent)";
    const [warn, bad] = zones.split(",").map(Number);
    if (!Number.isNaN(bad) && value >= bad) return "#fb7185";
    if (!Number.isNaN(warn) && value >= warn) return "#fbbf24";
    return "#34d399";
  }
}

customElements.define("gr-gauge", GrGauge);
